import * as React from 'react';

import {focusStatic} from '../helpers/focusStatic';
import {INTERACTIVE} from '../helpers/tabbable';
import {dialogContaining, focusFallback} from '../hooks/useDialogIsolation';

import type {FocusPlace, FocusRecovery, ListPlace} from './context';
import {FocusRecoveryContext} from './context';

// Focus recovery
//
// When the focused element leaves the document, or can no longer take focus
// because it was hidden or disabled, the browser puts focus on <body>, and the
// next Tab starts again at the top of the page (WCAG 2.4.3). That happens when
// a row is deleted and its controls go with it, when a spinner takes the place
// of the button that was pressed, when a list shows its loading state while it
// loads again, and when a step of a wizard gives way to the next one. This
// puts focus back in the same place, for every page at once, so that no list
// or wizard needs code of its own for it.
//
// A list opts in with `data-focus-group` on the element whose children are its
// items (for a table, the rows of its bodies). `Table`, `DataTable`, `ListView`
// and `ReorderableList` set it. Its value names the group. Each list on a page
// needs a name of its own, and the name has to stay the same when the list
// mounts again, as it does while it loads. When the group is gone, this takes
// a group with its name only if that group mounted after focus got to the
// list: another list with the same name is never it.
//
// A group can also be the steps of one flow, as in the audio uploader: one
// step shows at a time, and the next one takes the place of the last. Such a
// group has `data-focus-steps` too. Its children are not items, so focus never
// goes to a control in the child that is now at the same index (that would be
// a control of another step). It goes to the mark with the name of the group,
// which each step puts on its own heading (3 below). A mark with a name is
// never a mark for the page: focus that is lost outside the group does not go
// to it (4 below).
//
// On `focusin`, this records where focus is: the element, its ancestors, and
// in a list, the item that holds it and the control's index in that item.
// When the element has gone and focus is on <body>, it moves focus to the
// first of:
//
// 1. in a list, the same control in the item that now has the recorded index,
//    or another control in it, one that acts (such as its link) before an
//    indicator that only shows a tooltip; or the item itself, when it is the
//    same item and a spinner took the place of the control;
// 2. in a list, the same control in the item before it;
// 3. in a list, the element that the group's `aria-labelledby` names, else
//    the element marked `data-focus-fallback` with the name of the group (the
//    add button of a `ReorderableList`);
// 4. the nearest element marked `data-focus-fallback` with no name, around
//    where the element was: a list's heading, or the heading of the step of a
//    wizard that is now open. A mark with the name of a group is only that
//    group's;
// 5. the `h1` in <main>;
// 6. what `focusFallback` finds: the heading of an open dialog, say.
//
// When it had to use 3 or later for a list, and the list mounts again with
// items in it soon, before the user does anything, focus moves on to the item.
// A screen reader in browse mode reads on with no key or pointer event that
// the page can see, so the wait is short, and a scroll, a click or focus that
// leaves the fallback ends it too.
//
// Nothing moves when the element is still there and can take focus (the user
// clicked on text, and focus left it on purpose), or when another element has
// focus (a field with `autoFocus` on a new page). A dialog that closes after
// its opener went away asks this too, with the opener's place (see
// `useDialogIsolation`), so that one piece of code decides.

const GROUP = 'data-focus-group';
const STEPS = 'data-focus-steps';
const FALLBACK = 'data-focus-fallback';

// What can take focus in an item. Disabled controls count too, so that the
// same control has the same index in every row, and so does an element marked
// `data-focus-placeholder`, which holds the place of a control that a row
// leaves out. Neither of them takes focus.
const CONTROLS =
  'a[href], button, input, select, textarea, [tabindex], [data-focus-placeholder]';

// How long focus that went to a fallback waits for the list to come back with
// items in it, while it loads again: time for a list to load again after a
// deletion, and about the time that a screen reader takes to read the
// fallback. A user who reads on after it is not pulled back.
const WAIT_FOR_GROUP_MS = 2_000;

// A hidden helper, such as a menu's focus guard, is in a row only while the
// menu is open, or while it closes. It is not a control, and focus that passes
// through it keeps the place it had.
const isHelper = (element: Element, within?: Element) => {
  const hidden = element.closest(
    '[aria-hidden="true"], [data-floating-ui-focus-guard]',
  );
  return (
    Boolean(hidden) &&
    !element.hasAttribute('data-focus-placeholder') &&
    (!within || within.contains(hidden))
  );
};

// Whether the element is drawn. `checkVisibility` is missing in older browsers
// and in jsdom, which do with the styles of the element and its ancestors.
const isRendered = (element: Element): boolean => {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({visibilityProperty: true});
  }
  if (getComputedStyle(element).visibility === 'hidden') {
    return false;
  }
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (
      (node as HTMLElement).hidden ||
      getComputedStyle(node).display === 'none'
    ) {
      return false;
    }
  }
  return true;
};

/** Whether focus can go to the element, with a `tabindex` if it needs one. */
const canTakeFocus = (element: Element) =>
  element.isConnected &&
  !element.matches(':disabled') &&
  !element.closest('[inert]') &&
  isRendered(element);

/** Whether the element is a tab stop that can take focus now. */
const canFocus = (element: HTMLElement) =>
  element.tabIndex >= 0 && canTakeFocus(element);

const itemsOf = (group: Element): Array<Element> =>
  group instanceof HTMLTableElement
    ? Array.from(group.tBodies).flatMap(body => Array.from(body.rows))
    : Array.from(group.children);

const controlsOf = (item: Element) =>
  Array.from(item.querySelectorAll<HTMLElement>(CONTROLS)).filter(
    control => !isHelper(control, item),
  );

const groupsNamed = (name: string) =>
  Array.from(document.querySelectorAll<HTMLElement>(`[${GROUP}]`)).filter(
    group => group.getAttribute(GROUP) === name,
  );

const listPlaceOf = (element: Element): ListPlace | null => {
  const group = element.closest<HTMLElement>(`[${GROUP}]`);
  if (!group) {
    return null;
  }
  const name = group.getAttribute(GROUP)!;
  const named = groupsNamed(name);
  // The children of a group of steps are not items: no item to go back to.
  const items = group.hasAttribute(STEPS) ? [] : itemsOf(group);
  const itemIndex = items.findIndex(item => item.contains(element));
  const item = items[itemIndex] ?? null;
  const controls = item ? controlsOf(item) : [];
  const control = controls.indexOf(element as HTMLElement);
  return {
    control,
    controlFromEnd: control < 0 ? -1 : controls.length - 1 - control,
    group,
    item,
    itemIndex,
    name,
    named,
    nth: named.indexOf(group),
  };
};

const placeOf = (element: Element): FocusPlace => {
  const ancestors: Array<Element> = [];
  for (
    let node = element.parentElement;
    node && node !== document.body;
    node = node.parentElement
  ) {
    ancestors.push(node);
  }
  return {ancestors, list: listPlaceOf(element)};
};

// The group, or the one with its name that mounted in its place: it has the
// same position among the groups with that name, and it was not there when
// the place was recorded. A list with the same name that stays, or that is
// now in that position because the group is gone, is another list.
const findGroup = (list: ListPlace): HTMLElement | null => {
  if (list.group.isConnected) {
    return list.group;
  }
  const group = groupsNamed(list.name)[list.nth];
  return group && !list.named.includes(group) ? group : null;
};

// The same control as the one at `list`, in `item`. Rows can differ in how
// many controls they have (an indicator that only some rows show), so a
// control that was the last in its row is the last in this one too.
//
// When that control cannot take focus, or is only an indicator, another
// control in the item that acts (`INTERACTIVE`) takes it, such as its link.
// Focus on an indicator opens its tooltip, and a key does nothing there. In
// the item that held the control, the item itself is better than an
// indicator (see `itemTargets`): a spinner took the control's place.
const controlIn = (item: Element, list: ListPlace): HTMLElement | null => {
  const controls = controlsOf(item);
  const count = list.control + list.controlFromEnd + 1;
  const index =
    list.control < 0 || controls.length === count
      ? list.control
      : list.controlFromEnd === 0
        ? controls.length - 1
        : list.control;
  const candidates = [controls[index], ...controls].filter(
    control => control && canFocus(control),
  );
  return (
    candidates.find(control => control.matches(INTERACTIVE)) ??
    (item === list.item ? null : candidates[0]) ??
    null
  );
};

// The item that now has the recorded index, then the one before it.
const itemTargets = (group: Element, list: ListPlace) => {
  if (list.itemIndex < 0) {
    return [];
  }
  const items = itemsOf(group);
  const index = Math.min(list.itemIndex, items.length - 1);
  return [items[index], items[index - 1]].flatMap(item => {
    if (!item) {
      return [];
    }
    const control = controlIn(item, list);
    if (control) {
      return [control];
    }
    // A spinner took the place of the only control in the item that acts.
    return item === list.item &&
      item instanceof HTMLElement &&
      canTakeFocus(item)
      ? [item]
      : [];
  });
};

// The element that the group's `aria-labelledby` names. The group can be gone:
// its label need not be.
const labelOf = (group: Element): HTMLElement | null => {
  for (const id of (group.getAttribute('aria-labelledby') ?? '').split(' ')) {
    const label = id ? document.getElementById(id) : null;
    if (label) {
      return label;
    }
  }
  return null;
};

// A `data-focus-fallback` mark with a value stands only for the group with
// that name, as the add button of a `ReorderableList` does. A mark with no
// value stands for everything around it; a bare attribute in JSX has the value
// "true".
const isShared = (element: Element) =>
  ['', 'true'].includes(element.getAttribute(FALLBACK)!);

// The element marked `data-focus-fallback` with the name of the group. It is
// looked for each time, as a list can get it late: a `ReorderableList` shows
// its add button only when an item is not in the list.
const ownFallback = (list: ListPlace): HTMLElement | null =>
  Array.from(document.querySelectorAll<HTMLElement>(`[${FALLBACK}]`)).find(
    element =>
      element.getAttribute(FALLBACK) === list.name && canTakeFocus(element),
  ) ?? null;

// The nearest `data-focus-fallback` with no name around where the element was:
// in the nearest of its ancestors that is still in the document, or in the one
// that holds that, and so on up.
const fallbackNear = (place: FocusPlace): HTMLElement | null => {
  for (
    let node: Element | null =
      place.ancestors.find(ancestor => ancestor.isConnected) ?? null;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const found = Array.from(
      node.querySelectorAll<HTMLElement>(`[${FALLBACK}]`),
    ).find(element => isShared(element) && canTakeFocus(element));
    if (found) {
      return found;
    }
  }
  return null;
};

// Whether any of the element shows, in the viewport and in every ancestor
// that clips what overflows it.
const isOnScreen = (element: Element) => {
  let {bottom, left, right, top} = element.getBoundingClientRect();
  const clip = (box: {
    bottom: number;
    left: number;
    right: number;
    top: number;
  }) => {
    bottom = Math.min(bottom, box.bottom);
    left = Math.max(left, box.left);
    right = Math.min(right, box.right);
    top = Math.max(top, box.top);
  };
  for (let node = element.parentElement; node; node = node.parentElement) {
    const {overflowX, overflowY} = getComputedStyle(node);
    if (overflowX !== 'visible' || overflowY !== 'visible') {
      clip(node.getBoundingClientRect());
    }
  }
  clip({bottom: innerHeight, left: 0, right: innerWidth, top: 0});
  return top <= bottom && left <= right;
};

type FocusRecord = {
  element: Element;
  place: FocusPlace;
  // Focus left the element on purpose, as on a click on text.
  released: boolean;
};

const focusIsLost = () =>
  !document.activeElement || document.activeElement === document.body;

export const createFocusRecovery = (): FocusRecovery & {
  attach: () => () => void;
} => {
  let record: FocusRecord | null = null;
  // Set while focus is moved here: the place that focus keeps. After a
  // spinner took the menu's place, focus is on another control in the row,
  // and the next row's menu is still the one to go to.
  let moving: {place: FocusPlace} | null = null;
  // Focus went to a fallback while the list was gone or empty. The wait is
  // `settled` from the next frame on: a scroll before it comes from the change
  // itself (the page got shorter, or focus was scrolled to), and a scroll
  // after it from the user.
  let waiting: {
    list: ListPlace;
    settled: boolean;
    target: Element;
    until: number;
  } | null = null;

  const moveTo = (target: HTMLElement, place: FocusPlace) => {
    moving = {place};
    try {
      if (!focusStatic(target, {preventScroll: true})) {
        return false;
      }
    } finally {
      moving = null;
    }
    if (!isOnScreen(target)) {
      target.scrollIntoView?.({block: 'nearest', inline: 'nearest'});
    }
    return true;
  };

  // The place that focus keeps when it moves to `target` in an item.
  const placeFor = (target: HTMLElement, from: ListPlace): FocusPlace => {
    const place = placeOf(target);
    return {
      ...place,
      list: place.list && {
        ...place.list,
        control: from.control,
        controlFromEnd: from.controlFromEnd,
      },
    };
  };

  const recover = (place: FocusPlace | null): boolean => {
    waiting = null;
    const list = place?.list ?? null;
    const group = list && findGroup(list);
    if (list && group) {
      for (const target of itemTargets(group, list)) {
        if (moveTo(target, placeFor(target, list))) {
          return true;
        }
      }
    }
    const fallbacks = [
      group && labelOf(group),
      list && labelOf(list.group),
      list && ownFallback(list),
      place && fallbackNear(place),
      ...Array.from(document.querySelectorAll<HTMLElement>('main h1')),
    ];
    for (const target of fallbacks) {
      if (
        target &&
        canTakeFocus(target) &&
        moveTo(target, place ?? placeOf(target))
      ) {
        if (list) {
          const wait = {
            list,
            settled: false,
            target,
            until: Date.now() + WAIT_FOR_GROUP_MS,
          };
          waiting = wait;
          requestAnimationFrame(() => {
            wait.settled = true;
          });
        }
        return true;
      }
    }
    return false;
  };

  // Once the list is back with items in it, focus goes to the item.
  const resume = () => {
    const current = waiting;
    if (!current) {
      return;
    }
    if (
      document.activeElement !== current.target ||
      Date.now() > current.until
    ) {
      waiting = null;
      return;
    }
    const group = findGroup(current.list);
    for (const target of group ? itemTargets(group, current.list) : []) {
      if (moveTo(target, placeFor(target, current.list))) {
        waiting = null;
        return;
      }
    }
  };

  const recoverLost = (lost: FocusRecord) => {
    if (!recover(lost.place)) {
      focusFallback();
    }
  };

  const onMutation = () => {
    resume();
    const lost = record;
    if (
      !lost ||
      lost.released ||
      lost.element.isConnected ||
      !focusIsLost() ||
      // The element went with the dialog that held it, which is closing. The
      // dialog gives focus back to its opener, and asks for a place itself
      // when the opener is gone. (A row that goes from an open dialog leaves
      // the dialog, and focus moves as it does on the page.)
      dialogContaining(lost.element)
    ) {
      return;
    }
    recoverLost(lost);
  };

  const onFocusIn = (event: FocusEvent) => {
    const element = event.target;
    if (!(element instanceof Element)) {
      return;
    }
    if (moving) {
      record = {element, place: moving.place, released: false};
      return;
    }
    if (record && isHelper(element)) {
      record = {element, place: record.place, released: false};
      return;
    }
    waiting = null;
    record = {element, place: placeOf(element), released: false};
  };

  // Focus leaves an element for no other element. Chrome does that when the
  // element is removed, while it is still in the document, and when it is
  // hidden or disabled; so does a click on text. Which of these it was is
  // known only once the change is done.
  const onFocusOut = (event: FocusEvent) => {
    // Focus leaves the fallback: the user moved on.
    if (event.target === waiting?.target) {
      waiting = null;
    }
    const left = record;
    if (
      !left ||
      event.target !== left.element ||
      event.relatedTarget instanceof Element
    ) {
      return;
    }
    queueMicrotask(() => {
      if (record !== left || !left.element.isConnected) {
        // Focus went on, or the element went: the observer sees that.
        return;
      }
      const element = left.element;
      if (canTakeFocus(element)) {
        left.released = true;
      } else if (
        !isRendered(element) ||
        element.closest('[inert]') ||
        !element.matches(':disabled')
      ) {
        // Hidden, as the button of a step that has closed.
        if (focusIsLost()) {
          recoverLost(left);
        }
      }
      // Disabled but still shown, as while a form saves: focus is not moved
      // away from it. If it goes later, as with its row, the observer sees
      // that.
    });
  };

  // The user does something: focus that waits for a list stays where it is.
  // A screen reader in browse mode activates a control with a click and no
  // pointer event.
  const onInput = () => {
    waiting = null;
  };

  // The user scrolls, or a screen reader scrolls to what the user reads next.
  const onScroll = () => {
    if (waiting?.settled) {
      waiting = null;
    }
  };

  return {
    attach() {
      const observer = new MutationObserver(onMutation);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      document.addEventListener('focusin', onFocusIn);
      document.addEventListener('focusout', onFocusOut);
      document.addEventListener('keydown', onInput, true);
      document.addEventListener('pointerdown', onInput, true);
      document.addEventListener('click', onInput, true);
      // Scroll events do not bubble; in the capture phase, the document gets
      // the scrolls of every element too.
      document.addEventListener('scroll', onScroll, true);
      if (document.activeElement && !focusIsLost()) {
        record = {
          element: document.activeElement,
          place: placeOf(document.activeElement),
          released: false,
        };
      }
      return () => {
        observer.disconnect();
        document.removeEventListener('focusin', onFocusIn);
        document.removeEventListener('focusout', onFocusOut);
        document.removeEventListener('keydown', onInput, true);
        document.removeEventListener('pointerdown', onInput, true);
        document.removeEventListener('click', onInput, true);
        document.removeEventListener('scroll', onScroll, true);
        record = null;
        waiting = null;
      };
    },
    placeOf,
    recover,
  };
};

/**
 * Keeps focus in place when the focused element goes away (see the notes at
 * the top of this file). `StageProvider` renders it at the root of the
 * dashboard; another root can render it too. Inside another one, it does
 * nothing, so that one of them decides.
 */
export const FocusRecoveryProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const outer = React.useContext(FocusRecoveryContext);
  const [recovery] = React.useState(createFocusRecovery);
  React.useEffect(
    () => (outer ? undefined : recovery.attach()),
    [outer, recovery],
  );
  if (outer) {
    return <>{children}</>;
  }
  return (
    <FocusRecoveryContext.Provider value={recovery}>
      {children}
    </FocusRecoveryContext.Provider>
  );
};

export default FocusRecoveryProvider;
